import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Anchor, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { useTeamsOverview } from '../../../hooks/useTeamApi';
import { AvatarsList } from '../../../components/ui/AvatarsList';
import { TeamViewModal } from '../../../components/team/TeamViewModal';
import { Team, User } from '../../../types';
import { usePermission } from '../../../hooks/usePermission';

const MAX_TEAMS = 4;

// Лид и PM — указатели на команде, в members их нет; один человек может быть и тем и другим.
const peopleOf = (team: Team): User[] => {
  const people = [team.lead, team.pm, ...(team.members || []).map((m) => m.user)].filter(
    (u): u is User => u != null,
  );
  return people.filter((u, i) => people.findIndex((other) => other._id === u._id) === i);
};

/**
 * Команды одной строкой: название, лид с PM и аватары. Прежняя доска занимала пол-
 * экрана с поиском и раскрытием, хотя отвечала на один вопрос — «кто где».
 * За подробностями — раздел «Команды», ссылка внизу. Раздел админский, поэтому
 * остальным та же ссылка раскрывает полный список прямо здесь, а не ведёт в 403.
 */
export const TeamsCompact: React.FC<{ meId?: string }> = ({ meId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: teams } = useTeamsOverview();
  // Команду открываем той же модалкой, что и раздел «Команды»: уводить с
  // главной ради состава из четырёх человек незачем.
  const [openedTeam, setOpenedTeam] = React.useState<Team | null>(null);
  // /teams — по праву company.structure; остальным список раскрывается на месте.
  const isAdmin = usePermission('company.structure');
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (!teams?.length) return null;

  // Свои команды идут первыми: человек ищет на главной прежде всего их.
  const mine = (team: Team) => peopleOf(team).some((u) => u._id === meId);
  const sorted = [...teams].sort((a, b) => Number(mine(b)) - Number(mine(a)));
  const shown = isExpanded ? sorted : sorted.slice(0, MAX_TEAMS);

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        {t('dashboard.teams.title')}
      </Text>
      <Paper withBorder radius="md" className="todo-list">
        <Stack gap={0}>
          {shown.map((team) => (
            <UnstyledButton
              key={team._id}
              className="todo-list__item"
              onClick={() => setOpenedTeam(team)}
            >
              <Group gap="sm" wrap="nowrap" align="center">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>
                    {team.name}
                  </Text>
                  {/* Роль — отдельной колонкой: взгляд идёт по именам, а не разбирает строку. */}
                  <div className="team-roles">
                    {team.lead?.name && (
                      <>
                        <Text size="xs" c="dimmed">
                          {t('dashboard.teams.leadLabel')}
                        </Text>
                        <Text size="xs" truncate>
                          {team.lead.name}
                        </Text>
                      </>
                    )}
                    {team.pm?.name && (
                      <>
                        <Text size="xs" c="dimmed">
                          {t('dashboard.teams.pmLabel')}
                        </Text>
                        <Text size="xs" truncate>
                          {team.pm.name}
                        </Text>
                      </>
                    )}
                  </div>
                </div>
                <AvatarsList items={peopleOf(team)} />
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      </Paper>
      <TeamViewModal opened={!!openedTeam} onClose={() => setOpenedTeam(null)} team={openedTeam} />

      {sorted.length > shown.length && (
        <Anchor
          component="button"
          type="button"
          size="sm"
          onClick={() => (isAdmin ? navigate('/teams') : setIsExpanded(true))}
          style={{ alignSelf: 'flex-start' }}
        >
          {t('dashboard.teams.all', { count: sorted.length })}
        </Anchor>
      )}
    </Stack>
  );
};
